if (sessionStorage.redirect) {
  const redirect = sessionStorage.redirect;
  sessionStorage.removeItem("redirect");
  window.history.replaceState(null, "", redirect);
}

import React, { useState, useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useLocation,
  useNavigate,
  Navigate
} from "react-router-dom";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Pagination, Navigation } from "swiper/modules";
import useIsMobile from "./hooks/useIsMobile";
import { useAuth, useUser } from "@clerk/clerk-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faChartLine } from '@fortawesome/free-solid-svg-icons';

import HomePage from "./pages/HomePage";
import LessonsPage from "./pages/LessonsPage";
import SurahPage from "./pages/SurahPage";
import SurahListPage from "./pages/SurahListPage";
import AlphabetLesson from "./pages/AlphabetLesson";
import LongVowelsLesson from "./pages/LongVowelsLesson";
import ColorsLesson from "./pages/ColorsLesson";
import WordLesson from "./pages/WordLesson";
import JointLetters from "./pages/JointLetters";
import FlashcardsPage from "./pages/FlashcardsPage";
import ChaklLesson from "./pages/ChaklLesson";
import AnimalsLesson from "./pages/AnimalsLesson";
import AlphabetGame from "./pages/AlphabetGame";
import WordMatchGame from "./pages/WordMatchGame";
import AlphabetFormsGame from "./pages/AlphabetFormsGame";
import TracingGame from "./pages/TracingGame";
import WordsQuiz from "./pages/WordsQuiz";
import ConversationalLesson from "./pages/ConversationalLesson";
import GamePage from "./pages/GamePage";
import KidsSongsPage from "./pages/KidsSongsPage";
import PrayerLessonsPage from "./pages/PrayerLessonsPage";
import PrayerStepsPage from "./pages/PrayerStepsPage";
import FemalePrayerStepsPage from "./pages/FemalePrayerStepsPage";
import ProphetStoriesPage from "./pages/ProphetStoriesPage";
import SignUpPage from "./pages/SignUpPage";
import LoginPage from "./pages/LoginPage";
import Footer from "./components/Footer";
import NumbersLesson from "./pages/NumbersLesson";
import DaysLesson from "./pages/DaysLesson";
import FamilyLesson from "./pages/FamilyLesson";
import DashboardPage from "./pages/DashboardPage";
import DuasBar from "./components/DuasBar";
import WuduLesson from "./pages/WuduLesson";
import KidsLoader from "./components/KidsLoader";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DeleteAccount from "./pages/DeleteAccount";

// 🕌 Prayer Time Bar Component
function PrayerTimeBar() {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
          const { latitude, longitude } = coords;
          try {
            const res = await axios.get(
              `https://api.aladhan.com/v1/timings?latitude=${latitude}&longitude=${longitude}&method=2`
            );
            setPrayerTimes(res.data.data.timings);
          } catch {
            setError("Couldn't fetch prayer times.");
          }
        },
        () => setError("Location access denied.")
      );
    } else {
      setError("Geolocation not supported.");
    }
  }, []);

  if (!prayerTimes || error) return null;

  const keys = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];

  return (
    <div className="bg-gradient-to-r from-purple-500/90 via-pink-500/90 to-blue-500/90 backdrop-blur-xl text-white text-xs p-2 text-center shadow-lg border-b border-white/20">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center gap-1 mb-1">
          <div className="text-sm">🕌</div>
          <span className="font-semibold text-sm">Prayer Times</span>
        </div>
        <div className="flex flex-wrap justify-center gap-2 text-xs">
          {keys.map((k, i) => (
            <div key={k} className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
              <span className="font-medium">{k}:</span> 
              <span className="ml-1 font-bold">{prayerTimes[k]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileBottomNav() {
  const { isSignedIn, signOut } = useAuth();

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-600/95 via-pink-600/95 to-blue-600/95 backdrop-blur-xl border-t border-white/20 shadow-2xl md:hidden flex justify-around py-3 pb-safe z-50" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
      <Link to="/" className="flex flex-col items-center text-white hover:text-yellow-200 transition-all duration-300 hover:scale-105">
        <span className="text-xl">🏠</span>
        <span className="text-xs font-medium">Home</span>
      </Link>
      <Link to="/lessons" className="flex flex-col items-center text-white hover:text-yellow-200 transition-all duration-300 hover:scale-105">
        <span className="text-xl">📚</span>
        <span className="text-xs font-medium">Lessons</span>
      </Link>
      <Link to="/surah-list" className="flex flex-col items-center text-white hover:text-yellow-200 transition-all duration-300 hover:scale-105">
        <span className="text-xl">📖</span>
        <span className="text-xs font-medium">Quran</span>
      </Link>
      <Link to="/game" className="flex flex-col items-center text-white hover:text-yellow-200 transition-all duration-300 hover:scale-105">
        <span className="text-xl">🎮</span>
        <span className="text-xs font-medium">Games</span>
      </Link>
      {!isSignedIn && (
        <>
          <Link to="/login" className="flex flex-col items-center text-white hover:text-yellow-200 transition-all duration-300 hover:scale-105">
            <span className="text-xl">🔑</span>
            <span className="text-xs font-medium">Login</span>
          </Link>
          <Link to="/signup" className="flex flex-col items-center text-white hover:text-yellow-200 transition-all duration-300 hover:scale-105">
            <span className="text-xl">📝</span>
            <span className="text-xs font-medium">Sign Up</span>
          </Link>
        </>
      )}
    </div>
  );
}

function AppLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const user = localStorage.getItem("activeUser");
  const isMobile = useIsMobile();

  return (
    <>
      <div className={`${isMobile ? 'h-screen overflow-hidden' : 'min-h-screen'} flex flex-col bg-gradient-to-b from-yellow-50 to-pink-50`}>
        {/* Only show nav on desktop */}
        {!isMobile && (
          <nav className="bg-gradient-to-r from-purple-600/95 via-pink-600/95 to-blue-600/95 backdrop-blur-xl p-4 shadow-2xl border-b border-white/20">
            <div className="container mx-auto flex justify-between items-center">
              <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent hover:from-yellow-200 hover:to-white transition-all duration-300">
                🌙 Learn Arabic & Quran 🌟
              </Link>
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center space-x-6">
                <NavLinks user={user} />
              </div>
              {/* Hamburger Menu Button */}
              <button
                className="md:hidden text-white text-3xl focus:outline-none hover:scale-110 transition-transform"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                ☰
              </button>
            </div>
            {/* Mobile Menu */}
            {menuOpen && (
              <div className="md:hidden flex flex-col space-y-2 mt-4 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <NavLinks user={user} isMobile />
              </div>
            )}
          </nav>
        )}
        <main className={`${isMobile ? 'flex-1 overflow-y-auto pb-20' : 'flex-grow pb-32 md:pb-0'}`} style={isMobile ? { paddingBottom: 'max(5rem, calc(5rem + env(safe-area-inset-bottom)))' } : {}}>{children}</main>
        <div className="hidden md:block"><DuasBar /></div>
        <div className="hidden md:block"><Footer /></div>
      </div>
      <div className="md:hidden"><MobileBottomNav /></div>
    </>
  );
}

// Extracted NavLinks for cleaner JSX
function NavLinks({ user, isMobile }) {
  const { signOut } = useAuth();
  const { user: clerkUser } = useUser();
  const isSignedIn = !!clerkUser;

  return (
    <div className="flex items-center space-x-6">
      <Link to="/" className="text-white hover:text-yellow-200 transition-all duration-300 font-medium hover:scale-105">
        Home
      </Link>
      <Link to="/lessons" className="text-white hover:text-yellow-200 transition-all duration-300 font-medium hover:scale-105">
        Lessons
      </Link>
      <Link to="/game" className="text-white hover:text-yellow-200 transition-all duration-300 font-medium hover:scale-105">
        Games
      </Link>
      <Link to="/prophet-stories" className="text-white hover:text-yellow-200 transition-all duration-300 font-medium hover:scale-105">
        Prophet Stories
      </Link>
      {isSignedIn ? (
        <>
          <span className="text-white/90 font-medium">Welcome, {clerkUser.firstName || clerkUser.username}</span>
          <Link 
            to="/dashboard" 
            className="text-white hover:text-yellow-200 transition-all duration-300 p-2 hover:scale-110"
            title="Dashboard"
          >
            <FontAwesomeIcon icon={faChartLine} className="text-xl" />
          </Link>
          <button
            onClick={() => signOut()}
            className="text-white hover:text-yellow-200 transition-all duration-300 p-2 hover:scale-110"
            title="Sign Out"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="text-xl" />
          </button>
        </>
      ) : (
        <>
          <Link to="/login" className="text-white hover:text-yellow-200 transition-all duration-300 font-medium hover:scale-105">
            Login
          </Link>
          <Link to="/signup" className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full font-medium hover:bg-white/30 transition-all duration-300 hover:scale-105 border border-white/30">
            Sign Up
          </Link>
        </>
      )}
    </div>
  );
}

function LoadingFallback() {
  return <div className="p-4 text-center">Loading...</div>;
}

function App() {
  const { isLoaded } = useAuth();
  const isMobile = useIsMobile();

  if (!isLoaded) {
    return <KidsLoader />;
  }

  return (
    <Router>
      <div className={`${isMobile ? 'h-screen overflow-hidden' : 'min-h-screen'} flex flex-col bg-gradient-to-b from-yellow-50 to-pink-50`}>
        <div className="hidden md:block"><PrayerTimeBar /></div>
        <AppLayout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/lessons" element={<LessonsPage />} />
            <Route path="/surah/:id" element={<SurahPage />} />
            <Route path="/surah-list" element={<SurahListPage />} />
            <Route path="/alphabet-lesson" element={<AlphabetLesson />} />
            <Route path="/long-vowels-lesson" element={<LongVowelsLesson />} />
            <Route path="/colors-lesson" element={<ColorsLesson />} />
            <Route path="/word-lesson" element={<WordLesson />} />
            <Route path="/joint-letters" element={<JointLetters />} />
            <Route path="/flashcards" element={<FlashcardsPage />} />
            <Route path="/chakl-lesson" element={<ChaklLesson />} />
            <Route path="/animals-lesson" element={<AnimalsLesson />} />
            <Route path="/alphabet-game" element={<AlphabetGame />} />
            <Route path="/word-match-game" element={<WordMatchGame />} />
            <Route path="/alphabet-forms-game" element={<AlphabetFormsGame />} />
            <Route path="/tracing-game" element={<TracingGame />} />
            <Route path="/words-quiz" element={<WordsQuiz />} />
            <Route path="/conversational-lesson" element={<ConversationalLesson />} />
            <Route path="/game" element={<GamePage />} />
            <Route path="/kids-songs" element={<KidsSongsPage />} />
            <Route path="/prayer-lessons" element={<PrayerLessonsPage />} />
            <Route path="/prayer-steps" element={<PrayerStepsPage />} />
            <Route path="/female-prayer-steps" element={<FemalePrayerStepsPage />} />
            <Route path="/prophet-stories" element={<ProphetStoriesPage />} />
            <Route path="/numbers-lesson" element={<NumbersLesson />} />
            <Route path="/days-lesson" element={<DaysLesson />} />
            <Route path="/family-lesson" element={<FamilyLesson />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/wudu-lesson" element={<WuduLesson />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route 
              path="/deleteaccount" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <DeleteAccount />
                </Suspense>
              } 
            />
            <Route 
              path="/delete-account" 
              element={
                <Navigate to="/deleteaccount" replace />
              } 
            />
          </Routes>
        </AppLayout>
      </div>
    </Router>
  );
}

export default App;
